// Toma física de inventario — Inventario → Operaciones → Toma física.
//
// Un ajuste es el resultado; esto es el proceso. Cuatro cosas hacen que un
// conteo valga: el esperado se congela al abrir, se cuenta a ciegas, se
// recuenta lo que difiere, y el ajuste final es la DIFERENCIA (no el conteo),
// para que lo que se venda mientras se cuenta no se confunda con un faltante.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ClipboardCheck, Eye, EyeOff, ListChecks, Play, Trash2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Categoria, Location, Modal, SortTh, fieldCls, inv, invRpc, invalidateInventario, locationsList, money, num, useSort } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Conteo {
  id: string; codigo: string | null; tipo: string; estado: string; ciego: boolean;
  fecha: string; congelado_at: string; bodega: string | null; location_id: string;
  n_lineas: number; n_contadas: number; n_diferencias: number;
  valor_faltante: number; valor_sobrante: number; hash_acta: string | null; cerrado_at: string | null;
}
interface Linea {
  id: string; producto_id: string; codigo: string; producto: string; unidad_medida: string | null;
  ubicacion: string | null; esperado: number | null; contado: number | null; contado2: number | null;
  estado: string; nota: string | null; costo_unitario: number; diferencia: number | null;
}
interface Sugerido {
  producto_id: string; codigo: string; producto: string; clase: string;
  unidades: number; valor: number; ultimo_conteo: string | null;
  cada_dias: number; dias_sin_contar: number; prioridad: number;
}

const ESTADO: Record<string, { label: string; cls: string }> = {
  abierto:  { label: 'Contando',  cls: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  revision: { label: 'En revisión', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' },
  cerrado:  { label: 'Cerrado',   cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' },
  anulado:  { label: 'Anulado',   cls: 'bg-muted text-muted-foreground' },
};
const Pill = ({ estado }: { estado: string }) => {
  const e = ESTADO[estado] ?? { label: estado, cls: 'bg-muted text-muted-foreground' };
  return <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${e.cls}`}>{e.label}</span>;
};

export function TomaFisica() {
  const qc = useQueryClient();
  const [abierto, setAbierto] = useState<string | null>(null);
  return abierto
    ? <Detalle id={abierto} onVolver={() => { setAbierto(null); void qc.invalidateQueries({ queryKey: ['inv-conteos'] }); }} />
    : <Listado onAbrir={setAbierto} />;
}

// ===========================================================================
// Listado + apertura
// ===========================================================================
function Listado({ onAbrir }: { onAbrir: (id: string) => void }) {
  const qc = useQueryClient();
  const [nuevo, setNuevo] = useState(false);
  const { data: conteos = [], isLoading } = useQuery<Conteo[]>({
    queryKey: ['inv-conteos'], queryFn: () => invRpc<Conteo[]>('inv_conteos'),
  });
  const sort = useSort<Conteo>(conteos, (r, k) => (r as unknown as Record<string, string | number | null>)[k] ?? null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Toma física</h1>
          <p className="text-sm text-muted-foreground">
            El saldo se congela al abrir y el ajuste aplica sólo la diferencia: lo que se venda
            mientras se cuenta no se convierte en faltante.
          </p>
        </div>
        <Button onClick={() => setNuevo(true)}><ClipboardCheck className="mr-2 h-4 w-4" />Nuevo conteo</Button>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Conteos</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <p className="py-6 text-center text-sm text-muted-foreground">Cargando…</p>
          : conteos.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Todavía no se ha hecho ninguna toma física. Empieza por una bodega o por un conteo cíclico.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3 text-right">N°</th>
                    <SortTh col="codigo" sort={sort}>Código</SortTh>
                    <SortTh col="bodega" sort={sort}>Bodega</SortTh>
                    <SortTh col="tipo" sort={sort}>Tipo</SortTh>
                    <SortTh col="fecha" sort={sort}>Fecha</SortTh>
                    <SortTh col="n_lineas" sort={sort} align="right">Líneas</SortTh>
                    <SortTh col="n_contadas" sort={sort} align="right">Contadas</SortTh>
                    <SortTh col="n_diferencias" sort={sort} align="right">Difieren</SortTh>
                    <SortTh col="valor_faltante" sort={sort} align="right">Faltante</SortTh>
                    <SortTh col="valor_sobrante" sort={sort} align="right">Sobrante</SortTh>
                    <SortTh col="estado" sort={sort}>Estado</SortTh>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sort.sorted.map((c, i) => (
                    <tr key={c.id} className="border-b hover:bg-muted/40">
                      <td className="py-2 pr-3 text-right text-xs text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{c.codigo ?? '—'}</td>
                      <td className="py-2 pr-3">{c.bodega ?? '—'}</td>
                      <td className="py-2 pr-3 capitalize">{c.tipo}{c.ciego && <span className="ml-1 text-[11px] text-muted-foreground">· a ciegas</span>}</td>
                      <td className="py-2 pr-3">{c.fecha}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{c.n_lineas}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{c.n_contadas}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{c.n_diferencias || '—'}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-rose-600">{c.valor_faltante ? money(c.valor_faltante) : '—'}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-emerald-600">{c.valor_sobrante ? money(c.valor_sobrante) : '—'}</td>
                      <td className="py-2 pr-3"><Pill estado={c.estado} /></td>
                      <td className="py-2 pr-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => onAbrir(c.id)}>
                          {c.estado === 'cerrado' || c.estado === 'anulado' ? 'Ver acta' : 'Contar'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {nuevo && <ModalNuevo onClose={() => setNuevo(false)} onCreado={(id) => {
        setNuevo(false); void qc.invalidateQueries({ queryKey: ['inv-conteos'] }); onAbrir(id);
      }} />}
    </div>
  );
}

function ModalNuevo({ onClose, onCreado }: { onClose: () => void; onCreado: (id: string) => void }) {
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: categorias = [] } = useQuery<Categoria[]>({
    queryKey: ['inv-categorias-activas'],
    queryFn: () => inv.list<Categoria>('categorias', '?select=id,nombre,es_activo&es_activo=eq.true&order=nombre'),
  });
  const [loc, setLoc] = useState('');
  const [tipo, setTipo] = useState<'parcial' | 'ciclico' | 'total'>('parcial');
  const [ciego, setCiego] = useState(true);
  const [cat, setCat] = useState('');
  const [clase, setClase] = useState('');
  const [sugeridos, setSugeridos] = useState<string[] | null>(null);

  // Conteo cíclico: el módulo ya calcula la clasificación ABC, así que puede
  // decir qué toca contar hoy en vez de parar la bodega un domingo al año.
  const { data: sugerencia = [], isFetching } = useQuery<Sugerido[]>({
    queryKey: ['inv-conteo-sugerir', loc],
    queryFn: () => invRpc<Sugerido[]>('inv_conteo_sugerir', { _location: loc, _limite: 30 }),
    enabled: tipo === 'ciclico' && !!loc,
  });

  const crear = useMutation({
    mutationFn: () => invRpc<{ id: string; lineas: number; codigo: string }>('inv_conteo_crear', {
      payload: {
        location_id: loc, tipo, ciego,
        alcance: {
          ...(tipo === 'ciclico' && sugeridos?.length ? { productos: sugeridos } : {}),
          ...(tipo !== 'ciclico' && cat ? { categoria_id: cat } : {}),
          ...(tipo !== 'ciclico' && clase ? { clase } : {}),
        },
      },
    }),
    onSuccess: (r) => { toast.success(`Conteo ${r.codigo} abierto con ${r.lineas} línea(s)`); onCreado(r.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const listos = tipo === 'ciclico' ? (sugeridos?.length ?? 0) > 0 : !!loc;

  return (
    <Modal title="Nuevo conteo" onClose={onClose} wide>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo label="Bodega">
            <select className={fieldCls} value={loc} onChange={(e) => { setLoc(e.target.value); setSugeridos(null); }}>
              <option value="">Selecciona…</option>
              {bodegas.filter((b) => b.es_activo).map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Tipo">
            <select className={fieldCls} value={tipo} onChange={(e) => { setTipo(e.target.value as typeof tipo); setSugeridos(null); }}>
              <option value="parcial">Parcial — sólo lo que elijas</option>
              <option value="ciclico">Cíclico — lo que toca hoy según ABC</option>
              <option value="total">Total — toda la bodega, incluidos los que el sistema cree en cero</option>
            </select>
          </Campo>
        </div>

        {tipo !== 'ciclico' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo label="Categoría (opcional)">
              <select className={fieldCls} value={cat} onChange={(e) => setCat(e.target.value)}>
                <option value="">Todas</option>
                {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="Clase ABC (opcional)">
              <select className={fieldCls} value={clase} onChange={(e) => setClase(e.target.value)}>
                <option value="">Todas</option>
                <option value="A">A — las que mueven el dinero</option>
                <option value="B">B</option>
                <option value="C">C</option>
              </select>
            </Campo>
          </div>
        )}

        {tipo === 'ciclico' && (
          <div className="rounded-md border">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-sm font-medium">Toca contar hoy</span>
              <Button size="sm" variant="outline" disabled={!loc || isFetching}
                      onClick={() => setSugeridos(sugerencia.map((s) => s.producto_id))}>
                <Wand2 className="mr-2 h-3.5 w-3.5" />Tomar los {sugerencia.length}
              </Button>
            </div>
            {!loc ? <p className="p-4 text-sm text-muted-foreground">Elige la bodega.</p>
            : isFetching ? <p className="p-4 text-sm text-muted-foreground">Calculando…</p>
            : sugerencia.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">
                Nada vencido: los A se cuentan cada 30 días, los B cada 90 y los C cada 365, y ninguno
                ha pasado su plazo.
              </p>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 border-b bg-card text-left text-xs text-muted-foreground">
                    <tr><th className="px-3 py-2">Producto</th><th className="px-3 py-2">Clase</th>
                        <th className="px-3 py-2 text-right">Stock</th><th className="px-3 py-2 text-right">Valor</th>
                        <th className="px-3 py-2 text-right">Sin contar</th></tr>
                  </thead>
                  <tbody>
                    {sugerencia.map((s) => (
                      <tr key={s.producto_id} className="border-b last:border-0">
                        <td className="px-3 py-1.5"><span className="font-mono text-xs text-muted-foreground">{s.codigo}</span> {s.producto}</td>
                        <td className="px-3 py-1.5">{s.clase}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{num(s.unidades)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{money(s.valor)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                          {s.dias_sin_contar >= 9999 ? 'nunca' : `${s.dias_sin_contar} d`} <span className="text-[11px]">/ {s.cada_dias}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {sugeridos?.length ? <p className="border-t px-3 py-2 text-xs text-emerald-600">{sugeridos.length} producto(s) en el conteo.</p> : null}
          </div>
        )}

        <label className="flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm">
          <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-input" checked={ciego} onChange={(e) => setCiego(e.target.checked)} />
          <span>
            <span className="font-medium">Contar a ciegas</span>
            <span className="block text-xs text-muted-foreground">
              Quien cuenta no ve la cantidad del sistema. Es lo único que hace fiable un conteo:
              si se ve el número, se «encuentra» el número.
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!listos || crear.isPending} onClick={() => crear.mutate()}>
            <Play className="mr-2 h-4 w-4" />Abrir conteo
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ===========================================================================
// Detalle: contar, revisar, cerrar
// ===========================================================================
function Detalle({ id, onVolver }: { id: string; onVolver: () => void }) {
  const qc = useQueryClient();
  const [borrador, setBorrador] = useState<Record<string, string>>({});
  const [confirmar, setConfirmar] = useState<{ diferencias: number; faltante: number; sobrante: number; mensaje: string } | null>(null);
  const [motivo, setMotivo] = useState('');

  const { data, isLoading } = useQuery<{ cabecera: Conteo; lineas: Linea[] }>({
    queryKey: ['inv-conteo', id], queryFn: () => invRpc('inv_conteo', { _id: id }),
  });
  const cab = data?.cabecera;
  const lineas = useMemo(() => data?.lineas ?? [], [data]);
  const editable = cab?.estado === 'abierto' || cab?.estado === 'revision';
  // En revisión sólo se tocan las que quedaron marcadas para recontar.
  const visibles = cab?.estado === 'revision' ? lineas.filter((l) => l.estado === 'recontar' || l.contado2 != null) : lineas;

  const guardar = useMutation({
    mutationFn: () => invRpc<{ guardadas: number; contadas: number; lineas: number }>('inv_conteo_contar', {
      _id: id,
      _lineas: Object.entries(borrador).filter(([, v]) => v !== '').map(([k, v]) => ({ id: k, cantidad: Number(v) })),
    }),
    onSuccess: (r) => { toast.success(`${r.guardadas} línea(s) guardadas · ${r.contadas} de ${r.lineas}`); setBorrador({}); void qc.invalidateQueries({ queryKey: ['inv-conteo', id] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const revisar = useMutation({
    mutationFn: (recontar: boolean) => invRpc<{ mensaje: string }>('inv_conteo_revisar', { _id: id, _recontar: recontar }),
    onSuccess: (r) => { toast.success(r.mensaje); void qc.invalidateQueries({ queryKey: ['inv-conteo', id] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const simular = useMutation({
    mutationFn: () => invRpc<{ diferencias: number; faltante: number; sobrante: number; mensaje: string }>('inv_conteo_cerrar', { _id: id, _confirmar: false }),
    onSuccess: (r) => setConfirmar(r), onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({
    mutationFn: () => invRpc<{ mensaje: string }>('inv_conteo_cerrar', { _id: id, _confirmar: true, _motivo: motivo || null }),
    onSuccess: (r) => { toast.success(r.mensaje); setConfirmar(null); invalidateInventario(qc); void qc.invalidateQueries({ queryKey: ['inv-conteo', id] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: () => invRpc('inv_conteo_anular', { _id: id, _motivo: 'Anulado desde la pantalla' }),
    onSuccess: () => { toast.success('Conteo anulado'); onVolver(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading || !cab) return <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>;
  const pendientes = lineas.filter((l) => l.contado == null).length;
  const porRecontar = lineas.filter((l) => l.estado === 'recontar').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onVolver}><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              {cab.codigo} <Pill estado={cab.estado} />
              {cab.ciego && cab.estado === 'abierto' && <span className="flex items-center gap-1 text-xs font-normal text-muted-foreground"><EyeOff className="h-3.5 w-3.5" />a ciegas</span>}
            </h1>
            <p className="text-sm text-muted-foreground">
              {cab.bodega} · foto congelada el {new Date(cab.congelado_at).toLocaleString(LOCALE)} ·
              {' '}{cab.n_contadas} de {cab.n_lineas} contadas
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {editable && (
            <Button variant="outline" disabled={!Object.keys(borrador).length || guardar.isPending} onClick={() => guardar.mutate()}>
              Guardar cantidades
            </Button>
          )}
          {cab.estado === 'abierto' && (
            <Button disabled={pendientes > 0 || revisar.isPending} onClick={() => revisar.mutate(true)}
                    title={pendientes > 0 ? `Faltan ${pendientes} por contar` : 'Marca las diferencias para recontarlas'}>
              <ListChecks className="mr-2 h-4 w-4" />Revisar
            </Button>
          )}
          {cab.estado === 'revision' && (
            <Button disabled={porRecontar > 0 || simular.isPending} onClick={() => simular.mutate()}
                    title={porRecontar > 0 ? `Quedan ${porRecontar} por recontar` : 'Cerrar y ajustar'}>
              Cerrar y ajustar
            </Button>
          )}
          {editable && <Button variant="outline" onClick={() => anular.mutate()}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>

      {cab.estado === 'abierto' && pendientes > 0 && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          Faltan <strong>{pendientes}</strong> línea(s) por contar. Una línea en blanco no es un cero:
          si el producto no estaba, escribe <strong>0</strong>.
        </p>
      )}
      {cab.estado === 'revision' && porRecontar > 0 && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <strong>{porRecontar}</strong> línea(s) difieren y hay que recontarlas. El segundo conteo manda:
          así se separa el error de conteo del faltante real.
        </p>
      )}
      {cab.estado === 'cerrado' && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm dark:border-emerald-800 dark:bg-emerald-950/40">
          Cerrado el {new Date(cab.cerrado_at!).toLocaleString(LOCALE)} ·
          faltante <strong className="text-rose-600">{money(cab.valor_faltante)}</strong> ·
          sobrante <strong className="text-emerald-600">{money(cab.valor_sobrante)}</strong>
          <span className="ml-2 font-mono text-[11px] text-muted-foreground">acta {cab.hash_acta?.slice(0, 16)}</span>
        </div>
      )}

      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 text-right">N°</th>
                  <th className="py-2 pr-3">Código</th>
                  <th className="py-2 pr-3">Producto</th>
                  <th className="py-2 pr-3">Ubicación</th>
                  <th className="py-2 pr-3 text-right">
                    {cab.ciego && cab.estado === 'abierto'
                      ? <span className="flex items-center justify-end gap-1"><EyeOff className="h-3.5 w-3.5" />Sistema</span>
                      : <span className="flex items-center justify-end gap-1"><Eye className="h-3.5 w-3.5" />Sistema</span>}
                  </th>
                  <th className="py-2 pr-3 text-right">Contado</th>
                  {cab.estado !== 'abierto' && <th className="py-2 pr-3 text-right">Recuento</th>}
                  {cab.estado !== 'abierto' && <th className="py-2 pr-3 text-right">Diferencia</th>}
                  {cab.estado !== 'abierto' && <th className="py-2 pr-3 text-right">Valor</th>}
                </tr>
              </thead>
              <tbody>
                {visibles.map((l, i) => {
                  const campo = cab.estado === 'revision' ? l.contado2 : l.contado;
                  const val = borrador[l.id] ?? (campo != null ? String(campo) : '');
                  const real = l.contado2 ?? l.contado;
                  const dif = l.esperado != null && real != null ? real - l.esperado : null;
                  return (
                    <tr key={l.id} className={`border-b ${l.estado === 'recontar' ? 'bg-amber-50/60 dark:bg-amber-950/20' : ''}`}>
                      <td className="py-1.5 pr-3 text-right text-xs text-muted-foreground">{i + 1}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs">{l.codigo}</td>
                      <td className="py-1.5 pr-3">{l.producto}<span className="ml-1 text-[11px] text-muted-foreground">{l.unidad_medida}</span></td>
                      <td className="py-1.5 pr-3 text-xs text-muted-foreground">{l.ubicacion ?? '—'}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {l.esperado != null ? num(l.esperado) : <span className="text-muted-foreground">oculto</span>}
                      </td>
                      <td className="py-1.5 pr-3 text-right">
                        {editable && (cab.estado === 'abierto' || l.estado === 'recontar' || l.contado2 != null) ? (
                          <input type="number" min={0} step="0.0001" value={val}
                                 className="h-8 w-28 rounded-md border border-input bg-background px-2 text-right text-sm tabular-nums"
                                 onChange={(e) => setBorrador((b) => ({ ...b, [l.id]: e.target.value }))} />
                        ) : <span className="tabular-nums">{l.contado != null ? num(l.contado) : '—'}</span>}
                      </td>
                      {cab.estado !== 'abierto' && <td className="py-1.5 pr-3 text-right tabular-nums">{l.contado2 != null ? num(l.contado2) : '—'}</td>}
                      {cab.estado !== 'abierto' && (
                        <td className={`py-1.5 pr-3 text-right tabular-nums ${dif == null || dif === 0 ? '' : dif < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {dif == null ? '—' : dif === 0 ? '✓' : num(dif)}
                        </td>
                      )}
                      {cab.estado !== 'abierto' && (
                        <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                          {dif == null || dif === 0 ? '—' : money(dif * l.costo_unitario)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {confirmar && (
        <Modal title="Cerrar el conteo" onClose={() => setConfirmar(null)}>
          <div className="space-y-4">
            <p className="text-sm">{confirmar.mensaje}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md border p-3"><span className="block text-xs text-muted-foreground">Faltante</span>
                <span className="text-lg font-semibold tabular-nums text-rose-600">{money(confirmar.faltante)}</span></div>
              <div className="rounded-md border p-3"><span className="block text-xs text-muted-foreground">Sobrante</span>
                <span className="text-lg font-semibold tabular-nums text-emerald-600">{money(confirmar.sobrante)}</span></div>
            </div>
            <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
              Se aplicará la <strong>diferencia</strong> contra la foto del inicio, no el número contado:
              lo que se haya vendido mientras se contaba sigue en el kardex. El acta queda sellada y el
              conteo ya no se podrá anular.
            </p>
            <Campo label="Motivo / observación">
              <input className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                     placeholder="Ej.: toma física trimestral" />
            </Campo>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmar(null)}>Cancelar</Button>
              <Button disabled={cerrar.isPending} onClick={() => cerrar.mutate()}>Cerrar y ajustar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
